import { ActionStatus } from 'thunkless';
import { createSelector } from 'reselect';
import _ from 'lodash';

import type { ReduxState } from '@/redux/app/state';

export const inboxTicketsSelector = (subdomain: string, state: ReduxState) =>
  state.inbox.tickets[subdomain];

export const inboxTicketTotalCountSelector = (subdomain: string, state: ReduxState) =>
  state.inbox.ticketTotalCounts[subdomain];

export const inboxFiltersSelector = (state: ReduxState) =>
  state.inbox.filters;

export const inboxUsersSelector = (subdomain: string, state: ReduxState) =>
  state.inbox.users[subdomain];

export const inboxUserSelector = (subdomain: string, id: string, state: ReduxState) =>
  inboxUsersSelector(subdomain, state)?.[id];

export const inboxHiddenTicketsSelector = (subdomain: string, state: ReduxState) =>
  state.inbox.hiddenTickets[subdomain];

const inboxTopTagsObjectSelector = (subdomain: string, state: ReduxState) =>
  state.inbox.topTags[subdomain];

export const inboxTopTagsFetchStatusSelector = (subdomain: string, state: ReduxState) =>
  inboxTopTagsObjectSelector(subdomain, state)?.fetchStatus;

export const inboxTopTagsSelector = (subdomain: string, state: ReduxState) =>
  inboxTopTagsObjectSelector(subdomain, state)?.data;

export const inboxVisibleTicketsSelectorFactory = (
  subdomain: string,
  currentUserId?: string,
  isHelperBotAccount?: boolean,
) => createSelector(
  [
    _.partial(inboxTicketsSelector, subdomain),
    _.partial(inboxHiddenTicketsSelector, subdomain),
    (state: ReduxState) => state.ticket.data,
    () => isHelperBotAccount === true,
  ],
  (tickets, hiddenTickets, ticketData, helperBotAccount) => {
    const normalizedCurrentUserId = currentUserId != null ? String(currentUserId) : null;

    const visibleTickets = tickets?.filter((ticketUuid) => {
      if (hiddenTickets?.includes(ticketUuid)) return false;

      const ticketRecord = ticketData?.[ticketUuid];
      if (ticketRecord?.helper_bot !== true) return true;
      if (helperBotAccount) return true;

      const ticketOwnerId = ticketRecord?.user?.hash_id ?? ticketRecord?.user?.id;
      if (ticketOwnerId == null || normalizedCurrentUserId == null) return false;

      return String(ticketOwnerId) === normalizedCurrentUserId;
    }) ?? [];

    if (helperBotAccount || !visibleTickets.length) return visibleTickets;

    const pinnedTickets: string[] = [];
    const otherTickets: string[] = [];

    visibleTickets.forEach((ticketUuid) => {
      const ticketRecord = ticketData?.[ticketUuid];
      const isHelperBot = ticketRecord?.helper_bot === true;
      if (isHelperBot) pinnedTickets.push(ticketUuid);
      else otherTickets.push(ticketUuid);
    });

    return [...pinnedTickets, ...otherTickets];
  },
);

export const inboxLoadingStatusSelector = (state: ReduxState) =>
  state.inbox.loadingStatus;

export const inboxIsLoadingSelector = (state: ReduxState) =>
  inboxLoadingStatusSelector(state) === ActionStatus.BUSY;

export const inboxHasNextPageSelector = (state: ReduxState) =>
  state.inbox.hasNextPage;

export const inboxLastKeySelector = (state: ReduxState) =>
  state.inbox.lastKey;

export const inboxZoomedInMediaSelector = (state: ReduxState) =>
  state.inbox.zoomedInMedia;

export const inboxOrderSelector = (state: ReduxState) => state.inbox.filters.order;

export const inboxFilterCountSelector = (state: ReduxState) =>
  state.inbox.filterCount;
export const inboxSelectedTicketListSelector = (state: ReduxState) =>
  state.inbox.selectedTicketUuids;
export const inboxSelectedTicketSetSelector = createSelector(
  inboxSelectedTicketListSelector,
  (uuids) => uuids && new Set(uuids),
);
